import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtINR, fmtDate } from "../lib/api";
import { Page, Field, StatusPill } from "../components/Primitives";
import FileUploader from "../components/FileUploader";
import { toast } from "sonner";
import { Plus, X, ShieldCheck, ClipboardList, FileText, History, RefreshCw, Download } from "lucide-react";
import { motion } from "framer-motion";

const TYPE_LABEL = {
  showroom_sale: "Showroom Sale",
  service_upgrade: "Service Upgrade",
  part: "Part",
  extended: "Extended",
};

const COVERAGE_LABEL = {
  manufacturer: "Manufacturer",
  extended: "Extended",
  own_shop: "Karr Kraft",
  third_party: "Third Party",
};

const emptyForm = {
  type: "service_upgrade",
  coverage: "own_shop",
  provider: "",
  customer_id: "",
  customer_name: "",
  customer_phone: "",
  vehicle_reg: "",
  make: "",
  model: "",
  year: new Date().getFullYear(),
  vin: "",
  title: "",
  description: "",
  category: "",
  source_car_id: "",
  source_job_card_id: "",
  source_invoice_id: "",
  covered_items: [],
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  kms_limit: 0,
  start_kms: 0,
  cost: 0,
  status: "active",
  terms: "",
  doc_ids: [],
};

const TABS = [
  { key: "", label: "All" },
  { key: "showroom_sale", label: "Showroom Sales" },
  { key: "service_upgrade", label: "Service / Upgrades" },
  { key: "part", label: "Parts" },
  { key: "extended", label: "Extended" },
];

const STATUS_TABS = [
  { key: "", label: "All statuses" },
  { key: "active", label: "Active" },
  { key: "expiring", label: "Expiring (30d)" },
  { key: "expired", label: "Expired" },
  { key: "claimed", label: "Claimed" },
  { key: "void", label: "Void" },
];

export default function Warranty() {
  const [params, setParams] = useSearchParams();
  const typeFilter = params.get("type") || "";
  const statusFilter = params.get("status") || "";
  const sourceCarId = params.get("car") || "";
  const prefillJobCard = params.get("jobcard") || "";
  const vehicleReg = params.get("reg") || "";

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cars, setCars] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [claimsFor, setClaimsFor] = useState(null);
  const [claimsMode, setClaimsMode] = useState("add"); // "add" | "history"
  const [claimForm, setClaimForm] = useState({
    description: "", cost: 0, status: "open", notes: "", doc_ids: [],
    claim_date: new Date().toISOString().slice(0, 10),
  });
  const [renewFor, setRenewFor] = useState(null);
  const [renewForm, setRenewForm] = useState({ start_date: "", end_date: "", cost: 0, kms_limit: "", start_kms: "", notes: "" });
  const [jcParts, setJcParts] = useState([]);
  const [warrantyCategories, setWarrantyCategories] = useState([]);

  const load = async () => {
    const qs = {};
    if (typeFilter) qs.type = typeFilter;
    if (sourceCarId) qs.source_car_id = sourceCarId;
    if (vehicleReg) qs.vehicle_reg = vehicleReg;
    if (q) qs.q = q;
    if (statusFilter === "expiring") qs.expiring_days = 30;
    else if (statusFilter) qs.status = statusFilter;
    const { data } = await api.get("/warranties", { params: qs });
    setItems(data);
  };

  const loadSummary = async () => {
    const { data } = await api.get("/warranties-summary");
    setSummary(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [typeFilter, statusFilter, sourceCarId, vehicleReg]);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/showroom/cars").then(({ data }) => setCars(data));
    api.get("/job-cards").then(({ data }) => setJobCards(data));
    api.get("/settings/categories", { params: { scope: "warranty" } }).then(({ data }) => setWarrantyCategories(data));
    loadSummary();
  }, []);

  // Handle prefill from URL
  useEffect(() => {
    if (sourceCarId && cars.length > 0) {
      const car = cars.find((c) => c.id === sourceCarId);
      if (car && !open && params.get("new") === "1") {
        const today = new Date();
        const end = new Date();
        end.setFullYear(end.getFullYear() + 1);
        setForm({
          ...emptyForm,
          type: "showroom_sale",
          coverage: "own_shop",
          provider: "Karr Kraft",
          title: `Sale Warranty — ${car.make} ${car.model}`,
          vehicle_reg: car.registration_no || "",
          make: car.make,
          model: car.model,
          year: car.year,
          vin: car.vin || "",
          source_car_id: car.id,
          start_date: today.toISOString().slice(0, 10),
          end_date: end.toISOString().slice(0, 10),
          start_kms: car.kms || 0,
        });
        setEditing(null);
        setOpen(true);
      }
    }
    // eslint-disable-next-line
  }, [cars, sourceCarId]);

  useEffect(() => {
    if (prefillJobCard && jobCards.length > 0) {
      const jc = jobCards.find((x) => x.id === prefillJobCard);
      if (jc && !open) {
        const today = new Date();
        const end = new Date();
        end.setMonth(end.getMonth() + 6);
        // fetch parts for coverage selection
        api.get(`/job-cards/${jc.id}/parts`).then(({ data }) => {
          setJcParts(data.parts || []);
        }).catch(() => setJcParts([]));
        setForm({
          ...emptyForm,
          type: "service_upgrade",
          coverage: "own_shop",
          provider: "Karr Kraft",
          title: `Service Warranty — ${jc.code}`,
          customer_name: jc.customer_name || "",
          customer_phone: jc.customer_phone || "",
          vehicle_reg: jc.registration_no || "",
          make: jc.make || "",
          model: jc.model || "",
          source_job_card_id: jc.id,
          covered_items: [],
          start_date: today.toISOString().slice(0, 10),
          end_date: end.toISOString().slice(0, 10),
        });
        setEditing(null);
        setOpen(true);
      }
    }
    // eslint-disable-next-line
  }, [jobCards, prefillJobCard]);

  const setTab = (t) => {
    const nxt = new URLSearchParams(params);
    if (t) nxt.set("type", t); else nxt.delete("type");
    setParams(nxt);
  };
  const setStatus = (s) => {
    const nxt = new URLSearchParams(params);
    if (s) nxt.set("status", s); else nxt.delete("status");
    setParams(nxt);
  };

  const openCreate = () => {
    setEditing(null); setForm({ ...emptyForm });
    setOpen(true);
  };
  const openEdit = (w) => {
    setEditing(w.id);
    setForm({ ...emptyForm, ...w, cost: w.cost || 0, start_kms: w.start_kms || 0, kms_limit: w.kms_limit || 0 });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        year: +form.year || null,
        kms_limit: +form.kms_limit || null,
        start_kms: +form.start_kms || null,
        cost: +form.cost || 0,
      };
      if (editing) await api.put(`/warranties/${editing}`, payload);
      else await api.post("/warranties", payload);
      toast.success(editing ? "Warranty updated" : "Warranty created");
      setOpen(false); setEditing(null); load(); loadSummary();
      // Clear prefill params
      const nxt = new URLSearchParams(params);
      nxt.delete("new"); nxt.delete("jobcard");
      setParams(nxt);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this warranty?")) return;
    await api.delete(`/warranties/${id}`);
    toast.success("Deleted"); load(); loadSummary();
  };

  const downloadCertificate = async (w) => {
    try {
      const res = await api.get(`/warranties/${w.id}/certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `warranty-${w.code || w.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Certificate downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate certificate");
    }
  };

  const openClaimAdd = (w) => { setClaimsFor(w); setClaimsMode("add"); };
  const openClaimHistory = async (w) => {
    // Set mode first so the drawer renders in history mode immediately, even while details load
    setClaimsMode("history");
    setClaimsFor(w);
    try {
      const { data } = await api.get(`/warranties/${w.id}`);
      setClaimsFor(data);
    } catch { toast.error("Failed to load claims"); }
  };

  const openRenew = (w) => {
    const today = new Date().toISOString().slice(0, 10);
    const lastEnd = new Date(w.end_date);
    const nextEnd = new Date(lastEnd);
    nextEnd.setFullYear(nextEnd.getFullYear() + 1);
    setRenewFor(w);
    setRenewForm({
      start_date: today,
      end_date: nextEnd.toISOString().slice(0, 10),
      cost: w.cost || 0,
      kms_limit: w.kms_limit || 0,
    });
  };
  const doRenew = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/warranties/${renewFor.id}/renew`, {
        ...renewForm,
        cost: +renewForm.cost || 0,
        kms_limit: +renewForm.kms_limit || null,
      });
      toast.success("Warranty renewed");
      setRenewFor(null); load(); loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Renewal failed");
    }
  };

  const toggleCovered = (part) => {
    const key = part.item_id || part.description;
    const existing = form.covered_items.find((c) => (c.item_id || c.name) === key);
    if (existing) {
      setForm({ ...form, covered_items: form.covered_items.filter((c) => (c.item_id || c.name) !== key) });
    } else {
      setForm({
        ...form,
        covered_items: [
          ...form.covered_items,
          { name: part.description, item_id: part.item_id || null, qty: part.qty || 1, rate: part.rate || 0, notes: null },
        ],
      });
    }
  };
  const isCovered = (part) => {
    const key = part.item_id || part.description;
    return form.covered_items.some((c) => (c.item_id || c.name) === key);
  };

  const addClaim = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/warranties/${claimsFor.id}/claims`, {
        ...claimForm, cost: +claimForm.cost || 0,
      });
      toast.success("Claim logged");
      const { data } = await api.get(`/warranties/${claimsFor.id}`);
      setClaimsFor(data);
      setClaimForm({
        description: "", cost: 0, status: "open", notes: "", doc_ids: [],
        claim_date: new Date().toISOString().slice(0, 10),
      });
      load(); loadSummary();
    } catch (err) { toast.error("Failed"); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const countsByType = useMemo(() => summary?.by_type || {}, [summary]);

  return (
    <Page
      number="11"
      title="Warranties"
      subtitle="Every warranty on every car and every upgrade — one place to see coverage, claims and countdowns."
      right={
        <button className="kk-btn-primary" onClick={openCreate} data-testid="add-warranty">
          <Plus size={14} /> Add Warranty
        </button>
      }
    >
      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.button whileHover={{ y: -3 }} className="kk-tile p-5 text-left"
            onClick={() => setStatus("active")} data-testid="war-kpi-active">
            <div className="kk-overline">Active</div>
            <div className="font-display text-4xl mt-2">{summary.active}</div>
            <div className="text-xs text-[var(--kk-muted)] mt-1 uppercase tracking-wider">In-force</div>
          </motion.button>
          <motion.button whileHover={{ y: -3 }} className="kk-tile p-5 text-left"
            onClick={() => setStatus("expiring")} data-testid="war-kpi-expiring">
            <div className="kk-overline">Expiring ≤ 30d</div>
            <div className="font-display text-4xl mt-2">{summary.expiring_30d}</div>
            <div className="text-xs text-[var(--kk-muted)] mt-1 uppercase tracking-wider">Reach out</div>
          </motion.button>
          <motion.button whileHover={{ y: -3 }} className="kk-tile p-5 text-left"
            onClick={() => setStatus("claimed")} data-testid="war-kpi-claimed">
            <div className="kk-overline">Claimed</div>
            <div className="font-display text-4xl mt-2">{summary.claimed}</div>
            <div className="text-xs text-[var(--kk-muted)] mt-1 uppercase tracking-wider">Lifetime</div>
          </motion.button>
          <motion.button whileHover={{ y: -3 }} className="kk-tile p-5 text-left"
            onClick={() => setStatus("expired")} data-testid="war-kpi-expired">
            <div className="kk-overline">Expired</div>
            <div className="font-display text-4xl mt-2">{summary.expired}</div>
            <div className="text-xs text-[var(--kk-muted)] mt-1 uppercase tracking-wider">Archive</div>
          </motion.button>
        </div>
      )}

      {/* Tabs */}
      <div className="kk-tabs mb-4">
        {TABS.map((t) => (
          <button key={t.key || "all"} onClick={() => setTab(t.key)}
            className={"kk-tab " + (typeFilter === t.key ? "kk-tab-active" : "")}
            data-testid={`war-tab-${t.key || "all"}`}>
            {t.label} {t.key && countsByType[t.key] ? <span className="ml-1 text-[var(--kk-muted)]">· {countsByType[t.key]}</span> : null}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {STATUS_TABS.map((s) => (
          <button key={s.key || "all-status"} onClick={() => setStatus(s.key)}
            className={"kk-pill " + (statusFilter === s.key ? "kk-pill-black" : "kk-pill-muted")}
            data-testid={`war-status-${s.key || "all"}`}>
            {s.label}
          </button>
        ))}
        <div className="flex-1" />
        <input className="kk-input max-w-xs" placeholder="Search title / customer / reg"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} data-testid="war-search" />
      </div>

      <div className="overflow-x-auto">
        <table className="kk-table">
          <thead>
            <tr>
              <th>#</th><th>Title</th><th>Type</th><th>Coverage</th>
              <th>Customer / Vehicle</th>
              <th>Valid</th>
              <th>Kms</th>
              <th>Claims</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => {
              const end = new Date(w.end_date);
              const today = new Date();
              const daysLeft = Math.floor((end - today) / (1000 * 60 * 60 * 24));
              return (
                <tr key={w.id} data-testid={`war-row-${w.id}`}>
                  <td className="font-mono text-xs">{w.code}</td>
                  <td>
                    <div className="font-semibold">{w.title}</div>
                    <div className="text-xs text-[var(--kk-muted)]">{w.provider || "—"}</div>
                  </td>
                  <td><span className="kk-pill kk-pill-muted">{TYPE_LABEL[w.type] || w.type}</span></td>
                  <td className="text-xs">{COVERAGE_LABEL[w.coverage] || w.coverage}</td>
                  <td>
                    <div className="text-sm">{w.customer_name}</div>
                    <div className="text-xs font-mono text-[var(--kk-muted)]">{w.vehicle_reg} · {w.make} {w.model}</div>
                  </td>
                  <td className="text-xs font-mono">
                    <div>{fmtDate(w.start_date)}</div>
                    <div>→ {fmtDate(w.end_date)}</div>
                    {w.status === "active" && daysLeft >= 0 && (
                      <div className={"mt-1 " + (daysLeft <= 30 ? "text-[var(--kk-danger)] font-bold" : "")}>
                        {daysLeft}d left
                      </div>
                    )}
                  </td>
                  <td className="text-xs font-mono">
                    {w.kms_limit ? `${(w.start_kms || 0).toLocaleString()} → ${((w.start_kms || 0) + w.kms_limit).toLocaleString()}` : "—"}
                  </td>
                  <td className="font-mono text-center">{w.claim_count || 0}</td>
                  <td><StatusPill status={w.status} /></td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      <button onClick={() => openClaimAdd(w)} className="kk-pill kk-pill-muted" data-testid={`war-claim-${w.id}`}>
                        <ClipboardList size={11}/> Claim
                      </button>
                      <button onClick={() => openClaimHistory(w)} className="kk-pill kk-pill-muted" data-testid={`war-history-${w.id}`}>
                        <History size={11}/> History
                      </button>
                      <button onClick={() => openRenew(w)} className="kk-pill kk-pill-lime" data-testid={`war-renew-${w.id}`}>
                        <RefreshCw size={11}/> Renew
                      </button>
                      <button
                        onClick={() => downloadCertificate(w)}
                        className="kk-pill kk-pill-muted"
                        data-testid={`war-cert-${w.id}`}
                        title="Download signed PDF certificate"
                      >
                        <Download size={11}/> Cert
                      </button>
                      <button onClick={() => openEdit(w)} className="kk-pill kk-pill-muted">Edit</button>
                      <button onClick={() => remove(w.id)} className="kk-pill kk-pill-muted">✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-12 text-[var(--kk-muted)]">
                <ShieldCheck size={28} className="mx-auto mb-2" />
                No warranties recorded — add one from a sold car or a delivered job card.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit drawer */}
      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-[var(--kk-surface)] p-6 md:p-8 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="kk-overline">{editing ? "Edit" : "New"}</div>
                <h2 className="font-display text-3xl uppercase">{editing ? "Update Warranty" : "Add Warranty"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X/></button>
            </div>

            <div className="grid grid-cols-2 gap-5 mt-6">
              <Field label="Type *">
                <select className="kk-input" value={form.type} onChange={upd("type")} data-testid="war-type">
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Coverage">
                <select className="kk-input" value={form.coverage} onChange={upd("coverage")}>
                  {Object.entries(COVERAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Provider"><input className="kk-input" value={form.provider} onChange={upd("provider")} placeholder="e.g. BMW, Karr Kraft, Acko" /></Field>
              <Field label="Status">
                <select className="kk-input" value={form.status} onChange={upd("status")}>
                  <option value="active">Active</option>
                  <option value="claimed">Claimed</option>
                  <option value="void">Void</option>
                </select>
              </Field>

              <div className="col-span-2">
                <Field label="Category">
                  <select className="kk-input" value={form.category || ""} onChange={upd("category")} data-testid="war-cat-select">
                    <option value="">—</option>
                    {warrantyCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </Field>
              </div>

              <div className="col-span-2">
                <Field label="Title *"><input className="kk-input" required value={form.title} onChange={upd("title")} data-testid="war-title" placeholder="e.g. 1-year drivetrain warranty" /></Field>
              </div>

              <Field label="Customer">
                <select className="kk-input" value={form.customer_id} onChange={(e) => {
                  const c = customers.find((x) => x.id === e.target.value);
                  setForm({ ...form, customer_id: e.target.value, customer_name: c?.name || form.customer_name, customer_phone: c?.phone || form.customer_phone });
                }}>
                  <option value="">— pick or type below —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Customer name *"><input className="kk-input" required value={form.customer_name} onChange={upd("customer_name")} data-testid="war-cust-name" /></Field>
              <Field label="Customer phone"><input className="kk-input" value={form.customer_phone} onChange={upd("customer_phone")} /></Field>
              <Field label="Vehicle Reg #"><input className="kk-input" value={form.vehicle_reg} onChange={upd("vehicle_reg")} /></Field>
              <Field label="Make"><input className="kk-input" value={form.make} onChange={upd("make")} /></Field>
              <Field label="Model"><input className="kk-input" value={form.model} onChange={upd("model")} /></Field>
              <Field label="Year"><input className="kk-input" type="number" value={form.year} onChange={upd("year")} /></Field>
              <Field label="VIN"><input className="kk-input" value={form.vin} onChange={upd("vin")} /></Field>

              <Field label="Start date *"><input className="kk-input" type="date" required value={form.start_date} onChange={upd("start_date")} /></Field>
              <Field label="End date *"><input className="kk-input" type="date" required value={form.end_date} onChange={upd("end_date")} data-testid="war-end" /></Field>
              <Field label="Start KMs"><input className="kk-input" type="number" value={form.start_kms} onChange={upd("start_kms")} /></Field>
              <Field label="KMs limit"><input className="kk-input" type="number" value={form.kms_limit} onChange={upd("kms_limit")} /></Field>
              <Field label="Cost (₹)"><input className="kk-input" type="number" value={form.cost} onChange={upd("cost")} /></Field>

              {form.type === "showroom_sale" && (
                <Field label="Source Car">
                  <select className="kk-input" value={form.source_car_id} onChange={(e) => {
                    const c = cars.find((x) => x.id === e.target.value);
                    setForm({
                      ...form, source_car_id: e.target.value,
                      vehicle_reg: c?.registration_no || form.vehicle_reg,
                      make: c?.make || form.make, model: c?.model || form.model,
                      year: c?.year || form.year, vin: c?.vin || form.vin,
                      start_kms: c?.kms || form.start_kms,
                    });
                  }}>
                    <option value="">—</option>
                    {cars.map((c) => <option key={c.id} value={c.id}>{`${c.make} ${c.model} · ${c.registration_no}`}</option>)}
                  </select>
                </Field>
              )}
              {form.type !== "showroom_sale" && (
                <Field label="Source Job Card">
                  <select className="kk-input" value={form.source_job_card_id} onChange={async (e) => {
                    setForm({ ...form, source_job_card_id: e.target.value, covered_items: [] });
                    if (e.target.value) {
                      try {
                        const { data } = await api.get(`/job-cards/${e.target.value}/parts`);
                        setJcParts(data.parts || []);
                      } catch { setJcParts([]); }
                    } else setJcParts([]);
                  }}>
                    <option value="">—</option>
                    {jobCards.map((j) => <option key={j.id} value={j.id}>{`${j.code} · ${j.customer_name}`}</option>)}
                  </select>
                </Field>
              )}

              {form.source_job_card_id && jcParts.length > 0 && (
                <div className="col-span-2 border border-[var(--kk-border)] p-4 bg-[var(--kk-surface-2)]">
                  <div className="kk-overline mb-2">Select parts / items to cover under this warranty</div>
                  <p className="text-xs text-[var(--kk-muted)] mb-3">
                    Only the ticked parts will be covered. Consumables (oil, fluids, filters etc.) are typically left out.
                  </p>
                  <div className="space-y-2">
                    {jcParts.map((p, idx) => (
                      <label key={idx} className="flex items-center gap-3 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isCovered(p)}
                          onChange={() => toggleCovered(p)}
                          className="w-4 h-4"
                          data-testid={`covered-${idx}`}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{p.description}</div>
                          <div className="text-xs text-[var(--kk-muted)]">Qty {p.qty} · ₹{p.rate}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {form.covered_items.length > 0 && (
                    <div className="mt-3 text-xs text-[var(--kk-muted)]">
                      <strong className="text-[var(--kk-black)]">{form.covered_items.length}</strong> item(s) covered.
                    </div>
                  )}
                </div>
              )}

              {form.source_job_card_id && jcParts.length === 0 && (
                <div className="col-span-2 text-xs text-[var(--kk-muted)] border border-dashed border-[var(--kk-border)] p-3">
                  This job card has no parts. Warranty will cover the service as-is.
                </div>
              )}

              <div className="col-span-2">
                <Field label="Description"><textarea className="kk-input min-h-[60px]" value={form.description} onChange={upd("description")} /></Field>
              </div>
              <div className="col-span-2">
                <Field label="Terms & conditions"><textarea className="kk-input min-h-[60px]" value={form.terms} onChange={upd("terms")} /></Field>
              </div>
              <div className="col-span-2">
                <FileUploader
                  category="warranty"
                  ids={form.doc_ids}
                  onChange={(ids) => setForm({ ...form, doc_ids: ids })}
                  label="Documents (warranty certificate, annexures)"
                  accept="application/pdf,image/*"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button type="submit" className="kk-btn-primary" data-testid="save-warranty">
                {editing ? "Update" : "Create Warranty"}
              </button>
              <button type="button" className="kk-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Claims drawer */}
      {claimsFor && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setClaimsFor(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-[var(--kk-surface)] p-6 md:p-8 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="kk-overline">{claimsMode === "history" ? "Claim History · " : "Warranty · "}{claimsFor.code}</div>
                <h2 className="font-display text-2xl uppercase">{claimsFor.title}</h2>
                <div className="text-xs text-[var(--kk-muted)] mt-1">{claimsFor.customer_name} · {claimsFor.vehicle_reg}</div>
              </div>
              <button onClick={() => setClaimsFor(null)}><X/></button>
            </div>

            <div className="kk-overline mt-6 mb-2">Claim History ({(claimsFor.claims || []).length})</div>
            {(claimsFor.claims || []).length === 0 && (
              <div className="text-sm text-[var(--kk-muted)] py-4 border border-dashed border-[var(--kk-border)] text-center">No claims logged.</div>
            )}
            <ul className="space-y-3">
              {(claimsFor.claims || []).map((c) => (
                <li key={c.id} className="border-b border-[var(--kk-border)] pb-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{c.description}</div>
                    <StatusPill status={c.status} />
                  </div>
                  <div className="text-xs text-[var(--kk-muted)] font-mono mt-1">
                    {fmtDate(c.claim_date)} · by {c.logged_by} · {fmtINR(c.cost || 0)}
                  </div>
                  {c.notes && <div className="text-xs mt-1">{c.notes}</div>}
                </li>
              ))}
            </ul>

            {claimsMode === "add" && (
              <form onSubmit={addClaim} className="mt-6 border-t border-[var(--kk-border)] pt-5">
                <div className="kk-overline mb-3">Log a claim</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Field label="Description *">
                      <input className="kk-input" required value={claimForm.description}
                        onChange={(e) => setClaimForm({ ...claimForm, description: e.target.value })}
                        data-testid="claim-desc" />
                    </Field>
                  </div>
                  <Field label="Claim date"><input className="kk-input" type="date" value={claimForm.claim_date}
                    onChange={(e) => setClaimForm({ ...claimForm, claim_date: e.target.value })} /></Field>
                  <Field label="Cost (₹)"><input className="kk-input" type="number" value={claimForm.cost}
                    onChange={(e) => setClaimForm({ ...claimForm, cost: e.target.value })} /></Field>
                  <Field label="Status">
                    <select className="kk-input" value={claimForm.status}
                      onChange={(e) => setClaimForm({ ...claimForm, status: e.target.value })}>
                      <option value="open">Open</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="closed">Closed</option>
                    </select>
                  </Field>
                  <div className="col-span-2">
                    <Field label="Notes"><textarea className="kk-input min-h-[60px]" value={claimForm.notes}
                      onChange={(e) => setClaimForm({ ...claimForm, notes: e.target.value })} /></Field>
                  </div>
                </div>
                <button type="submit" className="kk-btn-primary mt-4" data-testid="save-claim">
                  <ClipboardList size={14}/> Log Claim
                </button>
              </form>
            )}

            {claimsMode === "history" && (
              <div className="mt-6 border-t border-[var(--kk-border)] pt-5 flex gap-2">
                <button type="button" className="kk-btn-primary" onClick={() => setClaimsMode("add")} data-testid="switch-to-claim-add">
                  <ClipboardList size={14}/> Log New Claim
                </button>
                <button type="button" className="kk-btn-ghost" onClick={() => setClaimsFor(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Renew modal */}
      {renewFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRenewFor(null)}>
          <form onSubmit={doRenew} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[var(--kk-surface)] p-6 md:p-8">
            <div className="kk-overline">Renew</div>
            <h2 className="font-display text-2xl uppercase">{renewFor.title}</h2>
            <div className="text-xs text-[var(--kk-muted)] mt-1">Previous: {fmtDate(renewFor.start_date)} → {fmtDate(renewFor.end_date)}</div>
            <div className="grid grid-cols-2 gap-4 mt-5">
              <Field label="New start date *"><input className="kk-input" type="date" required value={renewForm.start_date}
                onChange={(e) => setRenewForm({ ...renewForm, start_date: e.target.value })} data-testid="renew-start" /></Field>
              <Field label="New end date *"><input className="kk-input" type="date" required value={renewForm.end_date}
                onChange={(e) => setRenewForm({ ...renewForm, end_date: e.target.value })} data-testid="renew-end" /></Field>
              <Field label="Renewal cost (₹)"><input className="kk-input" type="number" value={renewForm.cost}
                onChange={(e) => setRenewForm({ ...renewForm, cost: e.target.value })} /></Field>
              <Field label="KMs limit"><input className="kk-input" type="number" value={renewForm.kms_limit}
                onChange={(e) => setRenewForm({ ...renewForm, kms_limit: e.target.value })} /></Field>
            </div>
            <div className="mt-4 p-3 bg-[var(--kk-surface-2)] text-xs">
              A new warranty record will be created (WAR-xxx) linked to this one as <strong>renewed_from</strong>. The old
              warranty will be marked expired. Covered items & documents carry over — edit the new one if needed.
            </div>
            <div className="flex gap-2 mt-5">
              <button type="submit" className="kk-btn-primary" data-testid="save-renew">
                <RefreshCw size={14}/> Renew Warranty
              </button>
              <button type="button" className="kk-btn-ghost" onClick={() => setRenewFor(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </Page>
  );
}
